(function () {
  const citiesByUrl = new Map();

  function normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function parseMoney(value) {
    const digits = String(value || '').replace(/[^\d]/g, '');
    return digits ? Number(digits) : 0;
  }

  function formatMoney(value) {
    const amount = parseMoney(value);
    if (!amount) return String(value || '').trim();

    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(amount);
  }

  function deliveryRange(days) {
    const deliveryDays = Math.max(1, Number(days) || 1);
    const min = Math.max(1, deliveryDays - 1);
    const max = deliveryDays + 1;

    return min === max ? `${min} día hábil` : `de ${min} a ${max} días hábiles`;
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      };

      return entities[char];
    });
  }

  function getShippingPrice(city, estimator) {
    const productPriceCents = Number(estimator.dataset.productPriceCents || 0);
    const thresholdCents = Number(estimator.dataset.freeThresholdCents || 0);
    const standardPrice = estimator.dataset.standardShippingPrice || '';
    const farPrice = estimator.dataset.farShippingPrice || '';
    const freeText = estimator.dataset.freeShippingText || 'Gratis';

    if (city && city.extras) {
      return {
        label: formatMoney(farPrice),
        status: 'paid',
      };
    }

    if (thresholdCents > 0 && productPriceCents >= thresholdCents) {
      return {
        label: freeText,
        status: 'free',
      };
    }

    return {
      label: formatMoney(standardPrice),
      status: 'paid',
    };
  }

  function iconSvg(icon) {
    const paths = {
      city: 'M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 10.5A3.5 3.5 0 1 1 12 5a3.5 3.5 0 0 1 0 7.5Z',
      department: 'M4 4h16v16H4V4Zm3 3v3h3V7H7Zm7 0v3h3V7h-3ZM7 14v3h3v-3H7Zm7 0v3h3v-3h-3Z',
      delivery: 'M3 7h11v9H3V7Zm11 3h4l3 4v2h-7v-6ZM7 20a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Zm10 0a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z',
      price: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 15h-2v-1.5a4.5 4.5 0 0 1-2.8-1.2l1.1-1.6c.9.7 1.8 1 2.7 1 .8 0 1.3-.3 1.3-.8 0-.6-.6-.8-1.9-1.1-1.8-.5-3-1.1-3-2.9 0-1.5 1-2.6 2.6-3V5h2v1.4c1 .2 1.8.6 2.5 1.2l-1 1.6a4 4 0 0 0-2.5-.9c-.7 0-1.1.3-1.1.8 0 .5.5.7 1.8 1.1 1.8.5 3.1 1.1 3.1 3 0 1.6-1 2.7-2.8 3.1V17Z',
    };

    return `<svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="${paths[icon] || paths.city}"></path></svg>`;
  }

  function resultTile(icon, label, value, modifier, meta) {
    const classes = ['m-product-shipping-estimator__tile'];
    if (modifier) classes.push(`m-product-shipping-estimator__tile--${modifier}`);

    return `
      <span class="${classes.join(' ')}">
        <span class="m-product-shipping-estimator__tile-icon">${iconSvg(icon)}</span>
        <span class="m-product-shipping-estimator__tile-copy">
          <span class="m-product-shipping-estimator__tile-label">${escapeHtml(label)}</span>
          <strong>${escapeHtml(value)}</strong>
          ${meta ? `<small>${escapeHtml(meta)}</small>` : ''}
        </span>
      </span>
    `;
  }

  async function loadCities(url) {
    if (!url) return [];
    if (!citiesByUrl.has(url)) {
      citiesByUrl.set(
        url,
        fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error('Could not load cities');
            return response.json();
          })
          .then((cities) =>
            Array.isArray(cities)
              ? cities.map((city) => ({
                  ...city,
                  search: normalizeText(`${city.name} ${city.normalized}`),
                }))
              : []
          )
      );
    }

    return citiesByUrl.get(url);
  }

  function findMatches(cities, query) {
    const normalizedQuery = normalizeText(query);
    if (!normalizedQuery) return cities.slice(0, 8);

    return cities
      .filter((city) => city.search.includes(normalizedQuery))
      .sort((a, b) => {
        const aName = normalizeText(a.name || a.normalized);
        const bName = normalizeText(b.name || b.normalized);
        const aStarts = aName.startsWith(normalizedQuery) ? 0 : 1;
        const bStarts = bName.startsWith(normalizedQuery) ? 0 : 1;
        return aStarts - bStarts || aName.localeCompare(bName);
      })
      .slice(0, 8);
  }

  function exactCity(cities, value) {
    const normalizedValue = normalizeText(value);
    return cities.find((city) => normalizeText(city.name) === normalizedValue || normalizeText(city.normalized) === normalizedValue);
  }

  function renderOptions(estimator, matches) {
    const input = estimator.querySelector('[data-shipping-city]');
    const dropdown = estimator.querySelector('[data-shipping-options]');
    if (!input || !dropdown) return;

    dropdown.innerHTML = '';

    if (!matches.length) {
      dropdown.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      return;
    }

    matches.forEach((city) => {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'm-product-shipping-estimator__option';
      option.setAttribute('role', 'option');
      option.innerHTML = `
        <span>${escapeHtml(city.name || city.normalized)}</span>
        <small>${escapeHtml(city.department || '')} · ${escapeHtml(city.code || '')}</small>
      `;
      option.addEventListener('mousedown', (event) => {
        event.preventDefault();
        selectCity(estimator, city);
      });
      dropdown.appendChild(option);
    });

    dropdown.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function selectCity(estimator, city) {
    const input = estimator.querySelector('[data-shipping-city]');
    const dropdown = estimator.querySelector('[data-shipping-options]');
    if (!input || !dropdown) return;

    estimator.selectedCity = city;
    input.value = city.name || city.normalized || '';
    dropdown.hidden = true;
    input.setAttribute('aria-expanded', 'false');
  }

  function renderResult(estimator, city) {
    const result = estimator.querySelector('[data-shipping-result]');
    const notice = estimator.querySelector('[data-shipping-notice]');
    if (!result) return;

    const shipping = getShippingPrice(city, estimator);
    result.classList.add('is-success');
    result.hidden = false;
    if (notice) notice.hidden = false;
    result.innerHTML = `
      ${resultTile('city', 'Ciudad', city.name || city.normalized, '', `Código ${city.code || ''}`)}
      ${resultTile('department', 'Departamento', city.department || '')}
      ${resultTile('delivery', 'Entrega', deliveryRange(city.delivery))}
      ${resultTile('price', 'Envío', shipping.label, shipping.status === 'free' ? 'free' : '')}
    `;
  }

  function setMessage(estimator, message, success) {
    const result = estimator.querySelector('[data-shipping-result]');
    const notice = estimator.querySelector('[data-shipping-notice]');
    if (!result) return;

    result.classList.toggle('is-success', Boolean(success));
    result.hidden = false;
    if (notice) notice.hidden = true;
    result.textContent = message;
  }

  async function initEstimator(estimator) {
    if (!estimator || estimator.dataset.shippingEstimatorReady === 'true') return;

    const input = estimator.querySelector('[data-shipping-city]');
    const button = estimator.querySelector('[data-shipping-calculate]');
    if (!input || !button) return;

    const cities = await loadCities(estimator.dataset.citiesUrl).catch(() => []);
    const emptyMessage = estimator.dataset.emptyEstimate || 'Escribe tu ciudad o municipio para ver el tiempo estimado.';
    const notFoundMessage = estimator.dataset.notFoundText || 'No encontramos ese destino. Revisa la ciudad o municipio.';

    const updateOptions = () => {
      estimator.selectedCity = null;
      renderOptions(estimator, findMatches(cities, input.value));
    };

    const calculate = () => {
      const city = estimator.selectedCity || exactCity(cities, input.value) || findMatches(cities, input.value)[0];
      if (!input.value.trim()) {
        setMessage(estimator, emptyMessage, false);
        return;
      }

      if (!city) {
        setMessage(estimator, notFoundMessage, false);
        return;
      }

      selectCity(estimator, city);
      renderResult(estimator, city);
    };

    input.addEventListener('input', updateOptions);
    input.addEventListener('focus', updateOptions);
    input.addEventListener('blur', () => {
      window.setTimeout(() => {
        const dropdown = estimator.querySelector('[data-shipping-options]');
        if (dropdown) dropdown.hidden = true;
        input.setAttribute('aria-expanded', 'false');
      }, 120);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        calculate();
      }
    });
    button.addEventListener('click', calculate);

    estimator.dataset.shippingEstimatorReady = 'true';
  }

  function initEstimators(container) {
    const root = container || document;
    root.querySelectorAll('[data-product-shipping-estimator]').forEach(initEstimator);
  }

  function setText(node, value) {
    if (!node) return;
    node.textContent = value || '';
    node.hidden = !value;
  }

  function updateBenefit(benefit, priceCents) {
    const thresholdCents = Number(benefit.dataset.minPriceCents || 0);
    const unmetAction = benefit.dataset.unmetAction || 'show';
    const originalTitle = benefit.dataset.originalTitle || '';
    const originalDescription = benefit.dataset.originalDescription || '';
    const unmetTitle = benefit.dataset.unmetTitle || originalTitle;
    const unmetDescription = benefit.dataset.unmetDescription || originalDescription;
    const belowThreshold = thresholdCents > 0 && priceCents < thresholdCents;
    const title = benefit.querySelector('[data-benefit-title]');
    const description = benefit.querySelector('[data-benefit-description]');

    if (belowThreshold && unmetAction === 'hide') {
      benefit.hidden = true;
      return;
    }

    benefit.hidden = false;

    if (belowThreshold && unmetAction === 'change') {
      setText(title, unmetTitle);
      setText(description, unmetDescription);
      return;
    }

    setText(title, originalTitle);
    setText(description, originalDescription);
  }

  function updateBenefits(priceCents, container) {
    const root = container || document;
    root.querySelectorAll('[data-product-benefit]').forEach((benefit) => updateBenefit(benefit, priceCents));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initEstimators());
  } else {
    initEstimators();
  }

  document.addEventListener('shopify:section:load', (event) => initEstimators(event.target));
  document.addEventListener('variant:changed', (event) => {
    const priceCents = Number(event.detail && event.detail.variant && event.detail.variant.price);
    if (!Number.isFinite(priceCents)) return;

    updateBenefits(priceCents);

    document.querySelectorAll('[data-product-shipping-estimator]').forEach((estimator) => {
      estimator.dataset.productPriceCents = String(priceCents);
      if (estimator.selectedCity) renderResult(estimator, estimator.selectedCity);
    });
  });
})();
